import { BadgeUserAccountStatus, CommaSeparator, LabelGender, Username } from '@/components/custom';
import { BannerUserInfosWarning } from '@/components/custom/bannerUserInfos';
import { Badge, Button, Card, Separator } from '@/components/ui';
import { IHooksUseRequestWarnUserParams } from '@/interfaces';
import { getTitleEnumValue } from '@/functions';
import { UserAccountStatusEnum } from '@/enums';
import { IReporter } from '@/lib/interface.ts';
import { useListenEvents } from '@/hooks';
import { Link } from 'react-router-dom';
import routes from '@/lib/routes';
import { cn } from '@/lib/utils';
import { useState } from 'react';

type TState = Pick<IReporter, 'reportAbuseWarningSent'>;

type TProps = {
	onWarnUserClick: () => void;
	className?: string;
	reporter: IReporter;
	reportedUserId: string;
};

const ReporterCardItem = ({ reporter, onWarnUserClick, className, reportedUserId }: TProps) => {

	const [state, setState] = useState<TState>({
		reportAbuseWarningSent: (
			(!reporter.reportAbuseWarningSent || Number.isNaN(reporter.reportAbuseWarningSent))
				? 0
				: parseInt(reporter.reportAbuseWarningSent as any)
		)
	});

	useListenEvents<IHooksUseRequestWarnUserParams>({
		type: 'userWarned',
		callback: eventData => {
			eventData?.reporterUserId === reporter.reporter &&
				setState(l => ({ ...l, reportAbuseWarningSent: (l.reportAbuseWarningSent + 1) }));
		}
	});

	return (
		<Card className={cn(
			`flex flex-col p-3 gap-2`,
			`md:flex-row md:items-end md:justify-between relative`,
			className
		)}>
			<div className={'flex flex-col gap-2 md:gap-0'}>
				<Link
					to={routes.nav.users.single.getHref(reporter.reporter)}
					className={'flex flex-col text-sm text-muted-foreground cursor-pointer'}>
					<div className='flex items-center space-x-1'>
						<Username
							user={reporter}
							badgeSize={16} />
						<BannerUserInfosWarning countWarningReceived={state.reportAbuseWarningSent} />
						<CommaSeparator />
						<LabelGender
							countryIso3={reporter.countryIso3}
							gender={reporter.gender} />
						<CommaSeparator />
						<Badge variant={'secondary'}>
							{getTitleEnumValue('ReportReasonEnum', reporter.reason) ?? reporter.reason}
						</Badge>
					</div>
				</Link>
				<Separator className={'my-2'} />
				<p className={'text-sm'}>
					{reporter.description}
				</p>
			</div>
			{
				reporter.accountStatus === UserAccountStatusEnum.ACTIVE &&
				<div className={'flex flex-row justify-end items-end w-full md:w-auto gap-2'}>
					{reporter.conversationId && (
						<Link
							to={routes.nav.reportConversation.getHref(reporter.conversationId!, reporter.reporter, reportedUserId)}
							target='_blank'
							rel='noreferrer'>
							<Button size={'sm'} variant={'secondary'}>
								Voir la conversation
							</Button>
						</Link>
					)}
					{/* a refund report comes from the store, not from a member who could be warned */}
					{reporter.source !== 'refund' && (
						<Button
							size={'sm'}
							variant={'secondary'}
							onClick={onWarnUserClick}
							className={'w-full text-orange-500 bg-orange-100 hover:bg-orange-200/80'}>
							Avertir
						</Button>
					)}
				</div>
			}
			<BadgeUserAccountStatus
				user={reporter}
				title='Cet utilisateur est inactif'
				isVisible={l => l.accountStatus !== UserAccountStatusEnum.ACTIVE}
				className={'text-nowrap px-2 py-0.5 text-xs rounded-sm absolute -top-1.5 -right-1'} />
		</Card>
	);

};

export default ReporterCardItem;
